import { createEntityAdapter, createSlice, DeepPartial, PayloadAction } from '@reduxjs/toolkit';

import { Organization, OrganizationStorageSize } from 'protocol/model/dto_organization_pb';
import { OrganizationStaff } from 'protocol/model/dto_organization_staff_pb';

import { LoadingStateType, SliceName } from '@/shared/config';

export const organizationAdapter = createEntityAdapter<DeepPartial<Organization>>({
  selectId: ({ ID }) => ID,
});

const initialState = {
  organization: {} as Organization,
  parentDSOOrganization: null as Organization | null,
  storageSize: null as OrganizationStorageSize | null,
  staff: [] as OrganizationStaff[],
  loading: 'idle' as LoadingStateType,
};

const organizationSlice = createSlice({
    name: SliceName.organization,
    initialState,
    reducers: {
      setOrganization: (state, { payload }: PayloadAction<Organization>) => {
        state.organization = payload;
      },
      setNewestOrganization: (state, { payload }: PayloadAction<Organization>) => {
        if (payload.Deleted?.Deleted) {
          state.organization = {} as Organization;
        }

        const currentRevisionNumber =
          state.organization?.Revision?.Number ?? 0;
        const payloadRevisionNumber = payload.Revision?.Number ?? 0;

        if (payloadRevisionNumber > currentRevisionNumber) {
          state.organization = payload;
        }
      },
      setParentDSOOrganization: (state, { payload }: PayloadAction<Organization>) => {
        state.parentDSOOrganization = payload;
      },
      setNewestParentDSOOrganization: (state, { payload }: PayloadAction<Organization>) => {
        const currentRevisionNumber =
          state.parentDSOOrganization?.Revision?.Number ?? 0;
        const payloadRevisionNumber = payload.Revision?.Number ?? 0;

        if (payloadRevisionNumber > currentRevisionNumber) {
          state.parentDSOOrganization = payload;
        }
      },
      setStorageSize: (state, { payload }: PayloadAction<OrganizationStorageSize>) => {
        state.storageSize = payload;
      },
      setStaff: (state, { payload }: PayloadAction<OrganizationStaff[]>) => {
        state.staff = payload;
      },
      setNewestStaff: (state, { payload }: PayloadAction<OrganizationStaff>) => {
        const existingIndex = state.staff.findIndex(
          (staff) => staff.ID === payload.ID,
        );

        if (existingIndex === -1) {
          state.staff.push(payload);
          return;
        }

        const currentRevision = state.staff[existingIndex]?.Revision?.Number ?? 0;
        const payloadRevision = payload.Revision?.Number ?? 0;

        if (payloadRevision > currentRevision) {
          state.staff[existingIndex] = payload;
        }
      },
      setLoading: (state, { payload }: PayloadAction<LoadingStateType>) => {
        state.loading = payload;
      },
      removeOrganization: (state) => {
        state.organization = {} as Organization;
        state.parentDSOOrganization = null;
        state.storageSize = null;
        state.staff = [];
      },
    },
  });

export const { actions } = organizationSlice;

export default organizationSlice.reducer;
